(() => {
  const config = window.csRouteProtectionConfig || {};
  const productId = Number(config.productId || 0);
  const intervalCents = Number(config.intervalCents || 30000);
  const baseVariantId = Number(config.baseVariantId || 0);
  const variants = Array.isArray(config.variants)
    ? config.variants
        .map((variant) => ({
          id: Number(variant.id || 0),
          price: Number(variant.price || 0),
          available: variant.available !== false,
        }))
        .filter((variant) => variant.id > 0 && variant.price > 0)
    : [];

  const OPT_OUT_KEY = 'cs-route-protection-opt-out-v1';
  let operationQueue = Promise.resolve();
  let queuedOperations = 0;

  function storageGet() {
    try {
      return window.sessionStorage.getItem(OPT_OUT_KEY) === 'true';
    } catch (error) {
      return false;
    }
  }

  function storageSet(value) {
    try {
      if (value) window.sessionStorage.setItem(OPT_OUT_KEY, 'true');
      else window.sessionStorage.removeItem(OPT_OUT_KEY);
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }
  }

  function optOut() {
    storageSet(true);
  }

  function clearOptOut() {
    storageSet(false);
  }

  function isProtectionItem(item) {
    if (!item) return false;
    if (Number(item.product_id) === productId) return true;
    return variants.some((variant) => variant.id === Number(item.variant_id || item.id));
  }

  function merchandiseSubtotal(cart) {
    return (cart?.items || []).reduce((total, item) => {
      if (isProtectionItem(item)) return total;
      return total + Number(item.final_line_price ?? item.line_price ?? 0);
    }, 0);
  }

  function desiredProtection(subtotal) {
    if (subtotal <= 0 || intervalCents <= 0) return null;

    // Protection is based only on merchandise subtotal, never product/item count.
    // $0.01–$299.99 = 1 interval, $300–$599.99 = 2, $600–$899.99 = 3, etc.
    const tiers = Math.max(1, Math.floor(subtotal / intervalCents) + 1);
    const baseVariant =
      variants.find((variant) => variant.available && variant.id === baseVariantId) ||
      variants.find((variant) => variant.available);

    if (!baseVariant) return null;

    return {
      variantId: baseVariant.id,
      quantity: tiers,
      desiredPrice: tiers * baseVariant.price,
    };
  }

  async function readJson(response, fallbackMessage) {
    const text = await response.text();
    let payload = {};
    try {
      payload = text ? JSON.parse(text) : {};
    } catch (error) {
      throw new Error(fallbackMessage);
    }
    if (!response.ok || payload.status || payload.errors) {
      throw new Error(payload.description || payload.message || payload.errors || fallbackMessage);
    }
    return payload;
  }

  async function fetchCart() {
    const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    return readJson(response, 'Unable to read the cart.');
  }

  async function postCart(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    return readJson(response, 'Unable to update shipping protection.');
  }

  async function fetchSections(sectionIds, sectionsUrl) {
    if (!sectionIds.length) return null;
    const url = new URL(sectionsUrl || window.location.pathname, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    return readJson(response, 'Unable to refresh the cart.');
  }

  function sectionRequest(sectionIds, sectionsUrl) {
    if (!sectionIds.length) return {};
    return {
      sections: sectionIds,
      sections_url: sectionsUrl || window.location.pathname,
    };
  }

  async function normalizeMutationState(payload, sectionIds, sectionsUrl) {
    if (Array.isArray(payload?.items) && typeof payload?.item_count !== 'undefined') {
      if (!sectionIds.length || payload.sections) return payload;
    }

    const cart = await fetchCart();
    if (payload?.sections) cart.sections = payload.sections;
    else if (sectionIds.length) cart.sections = await fetchSections(sectionIds, sectionsUrl);
    return cart;
  }

  async function removeProtectionItems(items, sectionIds, sectionsUrl) {
    if (!items.length) return null;
    const updates = {};
    items.forEach((item) => {
      updates[item.key || String(item.variant_id || item.id)] = 0;
    });
    const payload = await postCart(window.routes?.cart_update_url || '/cart/update.js', {
      updates,
      ...sectionRequest(sectionIds, sectionsUrl),
    });
    return normalizeMutationState(payload, sectionIds, sectionsUrl);
  }

  async function applyDesiredProtection(protectionItems, target, sectionIds, sectionsUrl) {
    const matching = protectionItems.find((item) => Number(item.variant_id || item.id) === target.variantId);
    const extras = protectionItems.filter((item) => item !== matching);

    if (matching && extras.length === 0 && Number(matching.quantity) === target.quantity) return null;

    if (matching) {
      const updates = {};
      updates[matching.key || String(matching.variant_id || matching.id)] = target.quantity;
      extras.forEach((item) => {
        updates[item.key || String(item.variant_id || item.id)] = 0;
      });
      const payload = await postCart(window.routes?.cart_update_url || '/cart/update.js', {
        updates,
        ...sectionRequest(sectionIds, sectionsUrl),
      });
      return normalizeMutationState(payload, sectionIds, sectionsUrl);
    }

    if (protectionItems.length) {
      await removeProtectionItems(protectionItems, [], sectionsUrl);
    }

    const payload = await postCart(window.routes?.cart_add_url || '/cart/add.js', {
      items: [
        {
          id: target.variantId,
          quantity: target.quantity,
          properties: { _AUTO_ADDED_PROTECTION: 'YES' },
        },
      ],
      ...sectionRequest(sectionIds, sectionsUrl),
    });
    return normalizeMutationState(payload, sectionIds, sectionsUrl);
  }

  async function performSync(options = {}) {
    if (!config.enabled || !productId || !variants.length) return null;

    const sectionIds = Array.isArray(options.sections) ? [...new Set(options.sections.filter(Boolean))] : [];
    const cart = options.cart?.items ? options.cart : await fetchCart();
    const protectionItems = (cart.items || []).filter(isProtectionItem);
    const subtotal = merchandiseSubtotal(cart);

    if (subtotal <= 0) {
      clearOptOut();
      return removeProtectionItems(protectionItems, sectionIds, options.sectionsUrl);
    }

    if (storageGet()) {
      return removeProtectionItems(protectionItems, sectionIds, options.sectionsUrl);
    }

    const target = desiredProtection(subtotal);
    if (!target) {
      console.warn('[Shipping protection] No available protection variant was found.');
      return null;
    }

    return applyDesiredProtection(protectionItems, target, sectionIds, options.sectionsUrl);
  }

  function sync(options = {}) {
    const canUseSnapshot = queuedOperations === 0;
    const queuedOptions = {
      ...options,
      cart: canUseSnapshot ? options.cart : null,
    };

    queuedOperations += 1;
    const run = operationQueue
      .catch(() => null)
      .then(() => performSync(queuedOptions));

    operationQueue = run.catch(() => null);

    return run
      .catch((error) => {
        console.error('[Shipping protection]', error);
        return { __error: true, message: error.message };
      })
      .finally(() => {
        queuedOperations = Math.max(0, queuedOperations - 1);
      });
  }

  function parseSection(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  function renderDrawerSections(state) {
    if (!state?.sections) return;

    const drawerHtml = state.sections['cart-drawer'];
    const drawer = document.querySelector('cart-drawer');
    const currentRoot = document.querySelector('#CartDrawer');
    const sourceRoot = parseSection(drawerHtml, '#CartDrawer');

    if (drawer && currentRoot && sourceRoot) {
      const oldScroller = currentRoot.querySelector('cart-drawer-items');
      const oldScrollTop = oldScroller?.scrollTop || 0;
      const currentInner = currentRoot.querySelector('.drawer__inner');
      const sourceInner = sourceRoot.querySelector('.drawer__inner');

      if (currentInner && sourceInner) currentInner.innerHTML = sourceInner.innerHTML;

      const sourceDrawer = sourceRoot.closest('cart-drawer') || parseSection(drawerHtml, 'cart-drawer');
      drawer.classList.toggle('is-empty', Boolean(sourceDrawer?.classList.contains('is-empty')));

      requestAnimationFrame(() => {
        const newScroller = drawer.querySelector('cart-drawer-items');
        if (newScroller) newScroller.scrollTop = Math.min(oldScrollTop, newScroller.scrollHeight);
      });
    }

    const bubbleHtml = state.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleSource = parseSection(bubbleHtml, '.shopify-section');
    if (bubble && bubbleSource) bubble.innerHTML = bubbleSource.innerHTML;
  }

  function setControlBusy(control, busy) {
    if (!control) return;
    control.classList.toggle('is-loading', busy);
    const toggle = control.querySelector('[data-route-protection-toggle]');
    if (toggle) toggle.disabled = busy;
  }

  window.CSRouteProtection = {
    sync,
    optOut,
    clearOptOut,
    isProtectionItem,
    renderDrawerSections,
  };

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('[data-route-protection-remove]')) optOut();
    },
    true
  );

  document.addEventListener('change', async (event) => {
    const toggle = event.target.closest('[data-route-protection-toggle]');
    if (!toggle) return;

    const control = toggle.closest('[data-route-protection-control]');
    const status = control?.querySelector('[data-route-protection-status]');
    const enabled = toggle.checked;
    const previousOptOut = storageGet();

    setControlBusy(control, true);
    if (enabled) clearOptOut();
    else optOut();

    const hasDrawer = Boolean(document.querySelector('cart-drawer'));
    const sections = hasDrawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];
    const state = await sync({ sections, sectionsUrl: window.location.pathname });

    if (state?.__error) {
      storageSet(previousOptOut);
      toggle.checked = !enabled;
      if (status) status.textContent = 'Unable to update shipping protection. Please try again.';
      setControlBusy(control, false);
      return;
    }

    if (state?.sections && hasDrawer) renderDrawerSections(state);
    else setControlBusy(control, false);
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (!config.enabled || !productId || !variants.length) return;
    const hasDrawer = Boolean(document.querySelector('cart-drawer'));
    const sections = hasDrawer ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];
    sync({ sections, sectionsUrl: window.location.pathname }).then((state) => {
      if (state?.sections && hasDrawer) renderDrawerSections(state);
    });
  });
})();
